import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Globe, Shield, Server, Download, RefreshCw, CheckCircle2 } from "lucide-react";

export default function AdminDomains() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    partner_domain: "",
    admin_domain: "",
    backend_url: "",
    cors_origins: "",
    acme_email: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/domain-config");
      setConfig(data.config);
      setForm(data.config);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/domain-config", form);
      setConfig(data.config);
      setForm(data.config);
      toast.success(data.message || "Domain config saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadCaddyfile = async () => {
    try {
      const { data } = await api.get("/admin/domain-config/caddyfile");
      const blob = new Blob([data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Caddyfile";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Caddyfile downloaded");
    } catch (err) {
      toast.error("Failed to download Caddyfile");
    }
  };

  const changed = config && JSON.stringify(form) !== JSON.stringify(config);

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto p-6 lg:p-10 space-y-8" data-testid="admin-domains-page">
        <div>
          <p className="overline">ADMIN</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">
            Domain & Environment
          </h1>
          <p className="text-muted-foreground mt-2">
            Configure your partner portal, admin portal, and backend domains.
            Changes take effect immediately — no redeploy needed for login enforcement and CORS.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" /> Loading...
          </div>
        ) : (
          <div className="space-y-8">
            {/* Partner Domain */}
            <section className="rounded-xl border border-border p-6 space-y-4" data-testid="section-partner-domain">
              <div className="flex items-center gap-3">
                <Globe className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-lg">Partner Portal Domain</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                The domain where partners and customers login. Partners are blocked from logging in on the admin domain.
              </p>
              <div className="space-y-2">
                <Label htmlFor="partner_domain">Domain</Label>
                <Input
                  id="partner_domain"
                  data-testid="input-partner-domain"
                  placeholder="flexpay.today"
                  value={form.partner_domain}
                  onChange={(e) => setForm({ ...form, partner_domain: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  DNS A record must point to your EC2 Elastic IP. Caddy will auto-provision HTTPS.
                </p>
              </div>
            </section>

            {/* Admin Domain */}
            <section className="rounded-xl border border-border p-6 space-y-4" data-testid="section-admin-domain">
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-lg">Admin Portal Domain</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                The domain where admins, approvers, and sub-admins login. Admin URLs are obfuscated on this domain.
              </p>
              <div className="space-y-2">
                <Label htmlFor="admin_domain">Domain</Label>
                <Input
                  id="admin_domain"
                  data-testid="input-admin-domain"
                  placeholder="dev.flexpay.today"
                  value={form.admin_domain}
                  onChange={(e) => setForm({ ...form, admin_domain: e.target.value })}
                />
              </div>
            </section>

            {/* Backend URL */}
            <section className="rounded-xl border border-border p-6 space-y-4" data-testid="section-backend-url">
              <div className="flex items-center gap-3">
                <Server className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-lg">Backend & CORS</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="backend_url">Public Backend URL</Label>
                  <Input
                    id="backend_url"
                    data-testid="input-backend-url"
                    placeholder="https://flexpay.today"
                    value={form.backend_url}
                    onChange={(e) => setForm({ ...form, backend_url: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Used for webhook URL generation in the admin panel.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cors_origins">CORS Origins</Label>
                  <Input
                    id="cors_origins"
                    data-testid="input-cors-origins"
                    placeholder="https://flexpay.today,https://dev.flexpay.today"
                    value={form.cors_origins}
                    onChange={(e) => setForm({ ...form, cors_origins: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated allowed origins, or * for any. Restrict in production.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="acme_email">ACME / Let's Encrypt Email</Label>
                <Input
                  id="acme_email"
                  data-testid="input-acme-email"
                  placeholder="admin@flexpay.today"
                  value={form.acme_email}
                  onChange={(e) => setForm({ ...form, acme_email: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Let's Encrypt sends certificate expiry warnings to this email.
                </p>
              </div>
            </section>

            {/* Actions */}
            <div className="flex items-center gap-4 flex-wrap">
              <Button
                data-testid="save-domain-config"
                onClick={save}
                disabled={saving || !changed}
                className="min-w-[140px]"
              >
                {saving ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : changed ? (
                  "Save changes"
                ) : (
                  <><CheckCircle2 className="h-4 w-4 mr-2" /> Saved</>
                )}
              </Button>
              <Button
                variant="outline"
                data-testid="download-caddyfile"
                onClick={downloadCaddyfile}
              >
                <Download className="h-4 w-4 mr-2" /> Download Caddyfile
              </Button>
            </div>

            {/* Info box */}
            <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4 text-sm space-y-2">
              <p className="font-semibold">After saving:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Login domain enforcement updates <strong>instantly</strong> (no redeploy)</li>
                <li>CORS origins update <strong>instantly</strong></li>
                <li>For Caddy (HTTPS proxy): click "Download Caddyfile" and replace your EC2's <code>deploy/ec2/Caddyfile</code>, then run <code>docker compose restart caddy</code></li>
                <li>For frontend build: re-run <code>REACT_APP_BACKEND_URL=https://yourdomain yarn build</code> if you changed the partner domain</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
